/* eslint-disable jsx-a11y/anchor-has-content,jsx-a11y/control-has-associated-label */
import React, { useContext, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import _ from 'lodash';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import { CardSettingsContext } from '../../../../contexts/card/settings/context';
import { USER_DETAILS_QUERY_KEY, USER_PREFERENCES } from '../../../../utilities/sharedQueries';
import MainBodyLayout from '../partials/MainBodyLayout';
import { REPORT_CARD_STATUS } from '../../../../utilities/constants';
import { cardSettingsScreens } from '../constants';
import { CARD_NETWORKS, cardProviders, mainCardTypes } from '../../constants';
import VisaLogo from '../../../../assets/images/visa.svg';
import MastercardLogo from '../../../../assets/images/mastercard.svg';
import { useSetting } from '../../../../utilities/sharedHooks/settings';

const ConfirmReplacementCardDetails = () => {
  const { t: translate } = useTranslation();
  const history = useHistory();
  const { state, openScreen } = useContext(CardSettingsContext);

  const { data: userInfo } = useQuery(USER_DETAILS_QUERY_KEY);
  const { data: userPref } = useQuery(USER_PREFERENCES);
  const userDisplayCurrency = userPref?.displayCurrency || 'EUR';

  const [supportEmailAddress] = useSetting('server_configurations', 'support_email_address');

  const userFirstName = state?.user?.firstName;
  const userLastName = state?.user?.lastName;
  const cardStatus = state?.cardStatus;
  const cardProvider = state?.user?.cardProvider;
  const mailingAddresses = state?.user?.mailingAddresses;
  const screenOptions = state?.screenOptions;

  const deliveryAddress = useMemo(() => {
    let address = '';
    if (mailingAddresses && mailingAddresses.length > 0) {
      // we should use mailing address as delivery address.
      const mailingAddress = mailingAddresses[0];
      address = `${mailingAddress.address_line_2}, ${mailingAddress.address_line_1}, ${mailingAddress.state}, ${mailingAddress.city}, ${mailingAddress.post_code}, ${userInfo.user.selected_country.displayName}`;
    } else {
      // use the default physical address.
      address = `${
        _.isEmpty(userInfo?.user?.house_number) ? `` : `${userInfo?.user?.house_number},`
      }${userInfo.user.street}, ${userInfo.user.city}, ${userInfo.user.post_code}, ${
        userInfo.user.selected_country.displayName
      }`;
    }

    return address;
  }, [mailingAddresses, userInfo]);

  const cardLogo = useMemo(() => {
    if (
      cardProvider === cardProviders.GALILEO &&
      cardStatus?.network?.toUpperCase() === CARD_NETWORKS.VISA
    ) {
      return VisaLogo;
    }
    return MastercardLogo;
  }, [cardProvider, cardStatus]);

  const closeFlow = () => {
    history.push('/dashboard/card/details');
  };

  const renderOnPrevClick = () => {
    if (cardStatus?.is_replacement_requested === false) {
      closeFlow();
    } else if (screenOptions?.state === REPORT_CARD_STATUS.DAMAGEDNOTWORK) {
      openScreen(cardSettingsScreens.REPORT_CARD_ISSUE, screenOptions);
    } else {
      openScreen(cardSettingsScreens.REPORT_CARD_CONFIRM);
    }
  };

  const editAddress = () => openScreen(cardSettingsScreens.EDIT_MAILING_ADDRESS);

  const CardInfo = useMemo(
    () => ({
      title:
        cardProvider === cardProviders.MONAVATE ||
        (cardProvider === cardProviders.GALILEO &&
          cardStatus?.network?.toUpperCase() === CARD_NETWORKS.MASTERCARD)
          ? translate('card_order_confirm_mastercard_title')
          : translate('card_order_confirm_visacard_title'),
      type: translate('card_type_label').replace(
        '__TYPE__',
        mainCardTypes.PHYSICAL.charAt(0).toUpperCase() + mainCardTypes.PHYSICAL.slice(1)
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cardProvider, cardStatus]
  );

  return (
    <MainBodyLayout
      withBack
      onPrevClick={renderOnPrevClick}
      title={translate('card_order_confirm_details_title')}>
      <div className="d-flex justify-content-between align-items-center mt-4">
        <p className="text-f5 text-dark p-0">
          {cardProvider !== cardProviders.GALILEO &&
            translate('card_order_confirm_details_subtitle_lost')}
          {cardProvider === cardProviders.GALILEO && (
            <Trans
              i18nKey="card_order_confirm_details_subtitle_lost_galileo"
              values={{
                email: supportEmailAddress,
              }}
              components={[
                <a
                  key="contact-link-2"
                  className="text-decoration-none"
                  href={`mailto:${supportEmailAddress}`}
                  rel="noreferrer"
                />,
              ]}
            />
          )}
        </p>
        {cardProvider !== cardProviders.GALILEO && (
          <div
            role="button"
            tabIndex="0"
            onClick={editAddress}
            onKeyDown={editAddress}
            className="text-f6 text-primary justify-content-end">
            {translate('edit_address')}
          </div>
        )}
      </div>
      <div className="rounded-extra bg-lighter p-3 mt-4">
        <div className="p-0">
          <label className="text-f6 text-shadow">
            {translate('card_order_confirm_details_full_name')}
          </label>
          <input
            readOnly
            value={`${userFirstName} ${userLastName}`}
            type="text"
            className="form-control bg-lighter px-0 border-0 border-shadow rounded-0 shadow-none"
          />
        </div>
        <div className="p-0 mt-2">
          <label className="text-f6 text-shadow">
            {translate('card_order_confirm_details_address')}
          </label>
          <div>{deliveryAddress}</div>
        </div>
        <div className="p-0 mt-2">
          <label className="text-f6 text-shadow">
            {translate('card_order_confirm_details_card_type')}
          </label>
          <div className="d-flex flex-row justify-content-between">
            <div>
              {CardInfo.title} {'Physical + Virtual card'}
            </div>
            <img src={cardLogo} alt="Mastercard" className="mx-2" />
          </div>
        </div>
        <div className="p-0 mt-2">
          <label className="text-f6 text-shadow">
            {translate('card_order_confirm_details_card_currency')}
          </label>
          <div>{userDisplayCurrency}</div>
        </div>
      </div>
      <div
        className={`d-flex flex-row mt-5 pt-3 ${
          isMobile ? 'position-fixed w-95 bg-light pb-3 pe-2' : 'mx-2 px-5'
        }`}
        style={{ bottom: '0', left: '0', zIndex: 100 }}>
        <button
          type="button"
          className="btn py-3 btn-primary w-100"
          onClick={() => {
            openScreen(cardSettingsScreens.CONFIRM_DELIVERY_TYPE, screenOptions);
          }}>
          {translate('confirm_button_label')}
        </button>
      </div>
    </MainBodyLayout>
  );
};

export default ConfirmReplacementCardDetails;
